// State machine for the "leave this Group" flow shared across Circles,
// Networks, and Spaces. Owns:
//   1. the confirmation dialog open/close state
//   2. the destructive mutation call + redirect-on-success
//   3. the inline error string that surfaces back to the consumer
//
// The mutation function is injected so call sites stay in charge of the wire
// call (the unified `useLeaveGroup` mutation routes by `kind` to the right
// endpoint); this hook is agnostic of the wire shape.

import { useState } from 'react';

import { useErrorMessage } from '../../lib/error-message';

export type GroupLeaveFlow = {
  readonly confirmOpen: boolean;
  readonly setConfirmOpen: (open: boolean) => void;
  readonly error: string | null;
  readonly openConfirm: () => void;
  readonly closeConfirm: () => void;
  readonly confirmLeave: () => void;
};

export function useGroupLeaveFlow({
  leave,
  fallbackMessage,
}: {
  // Caller passes a fire-and-forget mutator. The hook handles success/error.
  readonly leave: (handlers: {
    onSuccess: () => void;
    onError: (err: unknown) => void;
  }) => void;
  // Kind-specific fallback copy, supplied translated by the calling
  // component (the Group namespace has no generic leave-error key).
  readonly fallbackMessage: string;
}): GroupLeaveFlow {
  const describeError = useErrorMessage();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return {
    confirmOpen,
    setConfirmOpen,
    error,
    openConfirm: () => {
      setError(null);
      setConfirmOpen(true);
    },
    closeConfirm: () => setConfirmOpen(false),
    confirmLeave: () => {
      setConfirmOpen(false);
      setError(null);
      leave({
        onSuccess: () => {},
        onError: (err) => setError(describeError(err, fallbackMessage)),
      });
    },
  };
}
